import { DatePicker } from '@/src/shadComponents/ui/DatePicker';
import { Dialog, Transition } from '@headlessui/react';
import { FilePlus, Plus } from 'lucide-react';
import { Fragment, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import LocationSmartField from '@/src/Common/components/LocationSmartField';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';

interface CreateActivationModalProps {
    onDismiss: (id?: number) => void;
}

export default function CreateActivationModal({
    onDismiss,
}: CreateActivationModalProps) {
    const cancelButtonRef = useRef(null);
    const [newName, setNewName] = useState('');
    const [newDate, setNewDate] = useState(new Date());
    const [newLocation, setNewLocation] = useState('');
    //const [createFromTemplate, setCreateFromTemplate] = useState(false);
    const [templateId, setTemplateId] = useState<number | null>(null);

    return (
        <Transition.Root show={true} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-10"
                initialFocus={cancelButtonRef}
                onClose={() => onDismiss()}
                open={true}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-muted/500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-background text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                {!templateId ? (
                                    <div className="bg-background px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                        <div className="sm:flex sm:items-start">
                                            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary sm:mx-0 sm:h-10 sm:w-10">
                                                <FilePlus
                                                    className="h-6 w-6 text-primary-foreground"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                                <h3
                                                    className="text-base font-semibold text-foreground"
                                                >
                                                    Create Activation
                                                </h3>
                                                <div className="mt-2">
                                                    <p className="text-sm text-muted-foreground">
                                                        Create activation from
                                                        template, or create a
                                                        new activation from
                                                        scratch.
                                                    </p>
                                                </div>
                                            </div>
                                            <div></div>
                                        </div>
                                        <div className="ml-12 mt-2 p-2">
                                            <div className="grid grid-cols-2 gap-4">
                                                <NavLink to="create">
                                                    <div className="bg-muted p-4 rounded-lg text-center ring-1 ring-inset ring-border hover:ring-foreground/20 cursor-pointer">
                                                        <h4 className="text-lg font-semibold mb-4">
                                                            From Scratch
                                                        </h4>

                                                        <Plus className="h-6 w-6 mx-auto" />
                                                    </div>
                                                </NavLink>
                                                <div className="p-2 space-y-3">
                                                    <Button
                                                        variant="outline"
                                                        onClick={() =>
                                                            setTemplateId(1)
                                                        }
                                                        className="w-full"
                                                    >
                                                        ROMR Template &rarr;
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        onClick={() =>
                                                            setTemplateId(2)
                                                        }
                                                        className="w-full"
                                                    >
                                                        SB Default Template &rarr;
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                                {templateId ? (
                                    <div className="bg-background px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                        <div className="sm:flex sm:items-start">
                                            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary sm:mx-0 sm:h-10 sm:w-10">
                                                <FilePlus
                                                    className="h-6 w-6 text-primary-foreground"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                                <h3
                                                    className="text-base font-semibold text-foreground"
                                                >
                                                    Create Activation
                                                </h3>
                                                <div className="mt-2">
                                                    <p className="text-sm text-muted-foreground">
                                                        Please provide a name
                                                        and date for the new
                                                        activation.
                                                    </p>
                                                </div>
                                            </div>
                                            <div></div>
                                        </div>
                                        <div className="ml-12 mt-2 p-2 space-y-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="name">
                                                    Activation Name
                                                </Label>
                                                <Input
                                                    id="name"
                                                    name="name"
                                                    type="text"
                                                    required
                                                    value={newName}
                                                    onChange={(e) =>
                                                        setNewName(
                                                            e.target.value,
                                                        )
                                                    }
                                                    placeholder="Enter activation name"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="date">
                                                    Date
                                                </Label>
                                                <DatePicker
                                                    id="date"
                                                    date={newDate}
                                                    setDate={setNewDate}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="location">
                                                    Location
                                                </Label>
                                                <LocationSmartField
                                                    className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-foreground placeholder:text-muted-foreground focus:ring-0 sm:text-sm sm:leading-6"
                                                    name="location"
                                                    value={newLocation}
                                                    setValue={(
                                                        newValue: string,
                                                    ) =>
                                                        setNewLocation(newValue)
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                                <div className="bg-muted/50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-3">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => onDismiss()}
                                        ref={cancelButtonRef}
                                    >
                                        Cancel
                                    </Button>
                                    {templateId ? (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => setTemplateId(null)}
                                        >
                                            Back
                                        </Button>
                                    ) : null}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
